import { create } from 'zustand'
import { Plane, Vector3 } from 'three'
import { v4 as uuidv4 } from 'uuid'
import { persist } from 'zustand/middleware'
import { ISOMETRIC_CONSTS } from '@/lib/workers/constants'
import { PlaneVisualizer } from '@/lib/cad/PlaneVisualizer'
import { subVectorArrays } from './utils/cad'
import { initPlaneObject } from '../pages/CADPage/components/CADModel/CrossSectionControls'
import { useDocumentState } from './document'

import type { RawAssemblyTree } from './assembly'

const LOCAL_STORAGE_KEY = 'q20-cad-page-store'

export type Explosions = {
  [partId: string]: {
    translation: [number, number, number]
    position: [number, number, number]
    originalPosition: [number, number, number]
  }
}

export type SaveableExplosions = {
  id: string
  cadId: string
  explosions: Explosions
}

export enum SectionState {
  PREINIT = 'preinit',
  INIT = 'init',
  PREENABLE = 'preenable',
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  RESET = 'reset',
}

export enum ExplosionsState {
  DISABLED = 'DISABLED',
  INIT_CONTROLS = 'INIT_CONTROLS',
  ENABLED = 'ENABLED',
  DRAGGING = 'DRAGGING',
}

export enum CONTROLS {
  TRACKBALL_CONTROLS = 1,
  CAMERA_CONTROLS = 2,
}

export enum WAND {
  TRANSPARENCY = 'TRANSPARENCY',
}

export type WandButtonState = WAND | string | null

export type RenderMode = 'full-color' | 'outline' | 'black-and-white-wireframe'

export type CADPageState = StateVariables & StateActions

export type SelectionContext = 'assembly' | 'step' | 'cad'

export type StateVariables = {
  /* Camera & Scene */
  cameraAspect: number
  cameraPosition: number[]
  cameraFov: number
  cameraZoom: number
  cameraQuaternion: number[]
  cameraUp: number[]
  cameraNear: number | null
  cameraFar: number | null
  createdViews: string[]
  boundsMax: number | null
  sceneCenter: Vector3 | null
  target: number[]
  controlsId: number
  fitting: boolean
  canvasSize: {
    width: number
    height: number
  }
  /* Selected Parts */
  selectedParts: string[]
  hiddenParts: string[]
  highlightedPartUUID: string | null
  selectionContext: SelectionContext | null
  /* Wand & Highlighter Tool*/
  colorMap: {
    [key: string]: number
  }
  transparentParts: string[]
  wandSelected: WandButtonState
  highlighting: boolean
  highlightedParts: string[]
  highlightColor: number | null
  /* Explosions */
  isExplosionLinesEnabled: boolean
  explosions: Explosions
  explosionsState: ExplosionsState
  loadedExplosion: string
  savedExplosions: SaveableExplosions[]
  point: [number, number, number]
  /* Cross Sections */
  sectionState: SectionState
  clippingPlanes: Plane[]
  exclusiveCrossSectionMap: string[][]
  planeObject: PlaneVisualizer
  rotateEnabled: boolean
  translateEnabled: boolean
  /* CAD Page */
  isEditModeActive: boolean
  showAxis: boolean
  isRotating: boolean
  isDragging: boolean
  renderMode: RenderMode
  loadingProgress: number
  createViewButtonHover: boolean
  operationStep?: {
    documentId: string
    stepId: string
    selectFromCad: boolean
    isActive: boolean
  } | null
  filterScreenshotsByDocumentPageId?: string | null
  subAssemblyDocumentPageId?: string | null
  hasDocumentLoaded: boolean
  screenshotPanelOpen: boolean
  isRetakeModeActive: boolean
  isCapturingScreenshotFromDocumentPage: boolean
  retakeViewId: string | null
  retakeLocation: string | null
  captureLocation: string | null
  isAssemblyTreeOpen: boolean
  allPartsHidden: boolean
  expandedAssemblyTreeNodes: string[]
  cadLoaded: boolean
  assemblyNodeToScrollTo: string | null
  completedCadVersionUploads: string[] // list of cad-version ids
  boundsInitialized: boolean
  hidePreviousOperations: boolean
  showFullModel: boolean
  panelWidthsPx: {
    [panelId: string]: number
  }
}

const initialState: StateVariables = {
  /* Camera & Scene */
  cameraAspect: 1,
  cameraPosition: [],
  cameraFov: ISOMETRIC_CONSTS.FOV,
  cameraZoom: ISOMETRIC_CONSTS.ZOOM,
  cameraQuaternion: ISOMETRIC_CONSTS.QUATERNION,
  cameraUp: [0, 1, 0],
  cameraNear: null,
  cameraFar: null,
  createdViews: [],
  boundsMax: null,
  sceneCenter: null,
  target: [0, 0, 0],
  controlsId: CONTROLS.TRACKBALL_CONTROLS,
  fitting: false,
  canvasSize: { width: 0, height: 0 },
  /* Selected Parts */
  selectedParts: [],
  highlightedPartUUID: null,
  hiddenParts: [],
  selectionContext: null,
  /* Wand & Highlighter Tool*/
  transparentParts: [],
  colorMap: {},
  wandSelected: null,
  highlighting: false,
  highlightedParts: [],
  highlightColor: null,
  /* Explosions */
  isExplosionLinesEnabled: false,
  explosionsState: ExplosionsState.DISABLED,
  explosions: {},
  savedExplosions: [],
  loadedExplosion: '',
  point: [0, 0, 0],
  /* Cross Sections */
  sectionState: SectionState.PREINIT,
  clippingPlanes: [],
  exclusiveCrossSectionMap: [],
  planeObject: initPlaneObject(),
  rotateEnabled: true,
  translateEnabled: true,
  /* CAD Page */
  showAxis: false,
  isRotating: false,
  isDragging: false,
  isEditModeActive: false,
  renderMode: 'outline',
  operationStep: null,
  loadingProgress: 0,
  createViewButtonHover: false,
  filterScreenshotsByDocumentPageId: null,
  subAssemblyDocumentPageId: null,
  hasDocumentLoaded: false,
  screenshotPanelOpen: false,
  isRetakeModeActive: false,
  isCapturingScreenshotFromDocumentPage: false,
  retakeViewId: null,
  retakeLocation: null,
  captureLocation: null,
  isAssemblyTreeOpen: true,
  allPartsHidden: false,
  expandedAssemblyTreeNodes: [],
  cadLoaded: false,
  assemblyNodeToScrollTo: null,
  completedCadVersionUploads: [],
  boundsInitialized: false,
  hidePreviousOperations: false,
  showFullModel: false,
  panelWidthsPx: {},
}

export type StateActions = {
  /* CAD Page */
  setState: (state: Partial<CADPageState>) => void
  getState: () => CADPageState
  resetEditMode: () => void
  resetRetakeMode: () => void
  resetCaptureMode: () => void
  toggleAxis: () => void
  isHidden: (uuid: string) => boolean
  setLoadingProgress: (progress: number) => void
  setCreateViewButtonHover: (hover: boolean) => void
  reset: () => void
  addCreatedView: (viewId: string) => void
  /* Wand Tool*/
  isColored: (partId: string) => boolean
  isTransparent: (partId: string) => boolean
  isColor: (partId: string, color: string) => boolean
  updateAppearance: (partId: string, appearance: string) => void
  setWandSelected: (wandState: WandButtonState) => void
  resetHighlightedParts: () => void
  /* Selected Parts */
  pushSelectedPart: (uuid: string) => void
  /* Explosions */
  disableExplosions: () => void
  setExplosion: (
    instanceName: string,
    oldPositions: [number, number, number],
    newPosition: [number, number, number],
  ) => void
  /* Cross Sections */
  toggleSectioning: () => void
  disableSectioning: () => void
  saveNewExplosion: (cadId: string) => void
  toggleExplosions: () => void
  toggleAssemblyTreeNode: (uuid: string) => void
  scrollAssemblyNodeIntoView: (
    uuid: string | null,
    tree?: RawAssemblyTree,
  ) => void
  addCompletedCadVersionUpload: (cadVersionId: string) => void
  setPanelWidthPx: (panelId: string, width: number) => void
}

export const useCADPageStore = create<CADPageState>()(
  persist(
    (set, get) => ({
      ...initialState,

      reset: () => {
        set(({ savedExplosions }) => ({
          ...initialState,
          savedExplosions,
          createdViews: [],
          planeObject: initPlaneObject(),
        }))
      },

      setState: (state: Partial<CADPageState>) => {
        set(state)
      },
      resetEditMode: () => {
        const prevOperationStep = get().operationStep
        set({
          highlightedPartUUID: null,
          isEditModeActive: false,
          operationStep: prevOperationStep
            ? {
                ...prevOperationStep,
                selectFromCad: false,
              }
            : null,
          selectedParts: [],
          explosionsState: ExplosionsState.DISABLED,
          wandSelected: null,
        })
        get().disableSectioning()
      },
      resetRetakeMode: () => {
        const retakeLocation = get().retakeLocation
        set({
          isRetakeModeActive: false,
          retakeViewId: null,
          retakeLocation: null,
        })
        if (retakeLocation === 'screenshotPanel') {
          set({ screenshotPanelOpen: true })
        } else if (retakeLocation) {
          // Navigate to the retake location (strip ~ prefix for wouter)
          const path = retakeLocation.startsWith('~')
            ? retakeLocation.slice(1)
            : retakeLocation
          window.history.pushState({}, '', path)
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      },
      resetCaptureMode: () => {
        const captureLocation = get().captureLocation
        set({
          isCapturingScreenshotFromDocumentPage: false,
          captureLocation: null,
        })
        if (captureLocation) {
          // Navigate to the capture location (strip ~ prefix for wouter)
          let path = captureLocation.startsWith('~')
            ? captureLocation.slice(1)
            : captureLocation

          // Ensure path starts with /
          if (!path.startsWith('/')) {
            path = '/' + path
          }

          // Parse the capture location URL to extract the page_id
          const captureUrl = new URL(path, window.location.origin)
          const capturePageId = captureUrl.searchParams.get('page_id')

          // Update document state's currentPageInView with the captured page_id
          if (capturePageId) {
            useDocumentState.getState().setCurrentPageInView(capturePageId)
          }

          // Replace the browser URL with the capture location (pathname + search)
          window.history.replaceState(
            {},
            '',
            captureUrl.pathname + captureUrl.search,
          )
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      },
      getState: () => get(),
      toggleAxis: () => set((state) => ({ showAxis: !state.showAxis })),
      setLoadingProgress: (progress: number) =>
        set({ loadingProgress: progress }),
      setCreateViewButtonHover: (hover: boolean) => {
        set({ createViewButtonHover: hover })
      },
      addCreatedView: (viewId) => {
        set((state) => {
          state.createdViews.push(viewId)
          return state
        })
      },

      /*
       *
       * Selected Parts
       *
       */
      pushSelectedPart: (uuid: string) => {
        set((state) => ({
          selectedParts: [...new Set([...state.selectedParts, uuid])],
        }))
      },
      isHidden: (partId: string) => {
        return get().hiddenParts.includes(partId)
      },

      /*
       *
       * Explosions
       *
       */
      setExplosion: (
        partId: string,
        oldPositions: [number, number, number],
        newPosition: [number, number, number],
      ) =>
        set((state) => {
          const explosions = { ...state.explosions }
          if (explosions[partId]) {
            explosions[partId].position = newPosition
            explosions[partId].translation = subVectorArrays(
              newPosition,
              oldPositions,
            )
          } else {
            explosions[partId] = {
              translation: subVectorArrays(newPosition, oldPositions),
              position: newPosition,
              originalPosition: oldPositions,
            }
          }
          return { explosions }
        }),
      saveNewExplosion: (cadId: string) => {
        set((state) => {
          const newExplosions = JSON.parse(JSON.stringify(state.explosions))
          const newSaveableExplosions: SaveableExplosions = {
            cadId,
            id: uuidv4(),
            explosions: newExplosions,
          }
          return {
            savedExplosions: [...state.savedExplosions, newSaveableExplosions],
            loadedExplosion: newSaveableExplosions.id,
          }
        })
      },
      disableExplosions: () => {
        set(() => ({ explosionsState: ExplosionsState.DISABLED }))
      },
      toggleExplosions: () => {
        set(({ explosionsState }) => {
          if (explosionsState === ExplosionsState.DISABLED) {
            return {
              explosionsState: ExplosionsState.INIT_CONTROLS,
            }
          } else {
            return {
              explosionsState: ExplosionsState.DISABLED,
            }
          }
        })
      },

      /*
       *
       * Cross Sections
       *
       */
      disableSectioning: () => {
        set((state) => {
          if (state.sectionState === SectionState.ENABLED) {
            return { sectionState: SectionState.DISABLED }
          }
          return state
        })
      },
      toggleSectioning: () => {
        const { PREINIT, INIT, PREENABLE, DISABLED, RESET } = SectionState
        set(({ sectionState }) => {
          if (sectionState === PREINIT || sectionState === RESET) {
            return { sectionState: INIT, isEditModeActive: true }
          } else if (sectionState === DISABLED) {
            return { sectionState: PREENABLE, isEditModeActive: true }
          } else {
            return { sectionState: DISABLED, isEditModeActive: false }
          }
        })
      },

      /*
       *
       * Wand Tool
       *
       */
      setWandSelected: (wandState: WandButtonState) => {
        const currentState = get().wandSelected
        if (currentState === wandState) set({ wandSelected: null })
        else set({ wandSelected: wandState })
      },
      updateAppearance: (partId: string, appearance: string) => {
        set((state) => {
          let transparentParts = state.transparentParts
          let colorMap = state.colorMap

          if (appearance === WAND.TRANSPARENCY) {
            // If part is already transparent, remove transparency
            if (state.isTransparent(partId)) {
              transparentParts = transparentParts.filter(
                (part) => part !== partId,
              )
            } else {
              // If part is not transparent, make it transparent and remove color
              transparentParts = [...new Set([...transparentParts, partId])]
              colorMap = Object.fromEntries(
                Object.entries(colorMap).filter(([key]) => key !== partId),
              )
            }
          } else {
            // Remove part from transparent list if it exists
            if (state.isTransparent(partId)) {
              transparentParts = transparentParts.filter(
                (part) => part !== partId,
              )
            }

            // If part is already this color, remove color
            if (state.isColor(partId, appearance)) {
              colorMap = Object.fromEntries(
                Object.entries(colorMap).filter(([key]) => key !== partId),
              )
            } else {
              colorMap = {
                ...colorMap,
                [partId]: parseInt(appearance.slice(1), 16),
              }
            }
          }

          return { transparentParts, colorMap }
        })
      },
      isColored: (partId: string) => {
        return get().colorMap[partId] !== undefined
      },
      isColor: (partId: string, color: string) => {
        return get().colorMap[partId] === parseInt(color.slice(1), 16)
      },
      isTransparent: (partId: string) => {
        return get().transparentParts.includes(partId)
      },
      resetHighlightedParts: () => {
        set((state) => ({
          colorMap: Object.keys(state.colorMap)
            .filter((part) => !state.highlightedParts.includes(part))
            .reduce(
              (acc, key) => {
                acc[key] = state.colorMap[key]
                return acc
              },
              {} as { [key: string]: number },
            ),
          highlightedParts: [],
        }))
      },
      toggleAssemblyTreeNode: (uuid: string) => {
        set((state) => {
          const expanded = state.expandedAssemblyTreeNodes
          const isOpen = expanded.includes(uuid)
          return {
            expandedAssemblyTreeNodes: isOpen
              ? expanded.filter((id) => id !== uuid) // remove the uuid
              : [...expanded, uuid], // add the uuid
          }
        })
      },
      scrollAssemblyNodeIntoView: (
        uuid: string | null,
        tree?: RawAssemblyTree,
      ) =>
        set((state) => {
          if (!tree) {
            return {
              assemblyNodeToScrollTo: uuid,
            }
          }
          const expanded = [...state.expandedAssemblyTreeNodes]

          let node = tree?.nodes.find((n) => n.uuid == uuid)
          while (node) {
            const parent = tree?.nodes.find((n) => n.uuid == node?.parent)
            if (parent && !expanded.includes(parent.uuid)) {
              expanded.push(parent.uuid)
            }
            node = parent
          }

          return {
            assemblyNodeToScrollTo: uuid,
            expandedAssemblyTreeNodes: expanded,
          }
        }),
      addCompletedCadVersionUpload: (cadVersionId: string) => {
        return set((state) => {
          if (state.completedCadVersionUploads.includes(cadVersionId)) {
            return {}
          }

          return {
            completedCadVersionUploads: [
              ...state.completedCadVersionUploads,
              cadVersionId,
            ],
          }
        })
      },
      setPanelWidthPx: (panelId: string, width: number) => {
        set((state) => ({
          panelWidthsPx: {
            ...state.panelWidthsPx,
            [panelId]: width,
          },
        }))
      },
    }),

    /* Persist only Explosions */
    {
      name: LOCAL_STORAGE_KEY,
      partialize: (state) => ({ savedExplosions: state.savedExplosions }),
    },
  ),
)
